/**
 * Tech Support Co-Pilot API service.
 *
 * John Kirk's tech-support agent, reached through a profile-LOCKED door at
 * `/api/tech-support-copilot` (the backend forces the `tech_support_portal`
 * profile — a non-technical re-skin of the `kirk` agent with dual-KB access:
 * his product KB + the Quality Signals KB, both read+write). This is a TEXT-only
 * client: it reuses the Agent Console SSE wire contract + pure parsing helpers,
 * pointed at the tech-support-copilot endpoints. No voice channel.
 *
 * It deliberately reuses agentConsole.ts's pure parsing helpers + types rather
 * than duplicating the SSE plumbing.
 */
import api from './api';
import type { AttachmentMeta, ChainDetail, RecipientResult } from '../types/portal';
import {
  CONSOLE_DOOR_REQUEST_TIMEOUT_MS,
  cancelConsoleTurn,
  renameConsoleConversation,
  streamConsoleSse,
  type CancelTurnResponse,
  type ConsoleEvent,
  type ConversationCreate,
  type ConversationDetail,
  type ConversationRead,
  type ModelsResponse,
} from './agentConsole';

export type {
  ConsoleEvent,
  ConversationCreate,
  ConversationDetail,
  ConversationRead,
  ModelOption,
  ModelsResponse,
} from './agentConsole';

const BASE = '/tech-support-copilot';

export async function createConversation(
  body: ConversationCreate = {},
): Promise<ConversationRead> {
  const { data } = await api.post<ConversationRead>(`${BASE}/conversations`, body, {
    timeout: CONSOLE_DOOR_REQUEST_TIMEOUT_MS,
  });
  return data;
}

/**
 * Resume (or create) the ONE conversation bound to a Gmail thread. Selecting an
 * email in the panel calls this so the co-pilot keeps a single persistent
 * conversation per thread (and the agent is given that thread's context).
 */
export async function conversationForThread(
  emailThreadId: string,
  subject?: string,
  model?: string,
): Promise<ConversationRead> {
  const { data } = await api.post<ConversationRead>(
    `${BASE}/conversations/for-thread`,
    { email_thread_id: emailThreadId, subject, model },
  );
  return data;
}

/**
 * Resume (or create) the user's GENERAL conversation — the co-pilot chat outside
 * any email. Resolved from the backend (find-or-create on email_thread_id IS
 * NULL) so it is always distinct from every per-email thread-bound conversation.
 */
export async function generalConversation(
  model?: string,
): Promise<ConversationRead> {
  const { data } = await api.post<ConversationRead>(
    `${BASE}/conversations/general`,
    { model },
  );
  return data;
}

/** Selectable models + this door's (Kirk) default model, for the picker. */
export async function getModels(): Promise<ModelsResponse> {
  const { data } = await api.get<ModelsResponse>(`${BASE}/models`);
  return data;
}

export async function listConversations(
  opts: { limit?: number; offset?: number } = {},
): Promise<ConversationRead[]> {
  const params: Record<string, number> = {};
  if (opts.limit != null) params.limit = opts.limit;
  if (opts.offset != null) params.offset = opts.offset;
  const { data } = await api.get<{ items: ConversationRead[] }>(
    `${BASE}/conversations`,
    { params },
  );
  return data.items;
}

export async function getConversationDetail(
  conversationId: string,
): Promise<ConversationDetail> {
  const { data } = await api.get<ConversationDetail>(
    `${BASE}/conversations/${conversationId}`,
    { timeout: CONSOLE_DOOR_REQUEST_TIMEOUT_MS },
  );
  return data;
}

/** Rename a Tech Support Co-Pilot conversation (editable title) via the shared path. */
export function renameConversation(conversationId: string, title: string) {
  return renameConsoleConversation(BASE, conversationId, title);
}

/**
 * Stop the conversation's in-flight turn (the composer's Stop button). Pass the
 * `runId` from the turn's `started` frame so a stale Stop can't abort a newer
 * turn. Idempotent — `{cancelled: false}` (the turn already finished) is a
 * normal outcome, not an error. The server emits the terminal `cancelled` +
 * `done` frames on the existing stream, which settle the UI.
 */
export function cancelMessage(
  conversationId: string,
  runId?: string | null,
): Promise<CancelTurnResponse> {
  return cancelConsoleTurn(BASE, conversationId, runId);
}

// --- Mail panel (live read of the bound Tech Support inbox) ----------------

/** One inbox row in the Co-Pilot email panel. */
export interface MailThreadSummary {
  thread_id: string;
  from_name: string;
  from_address: string | null;
  subject: string | null;
  snippet: string;
  received_at: string | null;
  unread: boolean;
  /** Newest message is our own reply (customer answered) — the ↩ Replied badge. */
  replied?: boolean;
  /** Raw To header of the newest message — the Sent folder shows recipients. */
  to_address?: string | null;
}

export interface MailThreadList {
  account_email: string;
  threads: MailThreadSummary[];
  /** Continue with this token for the next page; null on the last page. */
  next_page_token?: string | null;
  /** Unread conversations across the WHOLE inbox for this user (the Inbox tab
   *  badge); null when the server could only see one page. */
  unread_count?: number | null;
}

/** One conversation's per-user read state (POST /mail/thread/{id}/read). */
export interface ThreadReadStatus {
  thread_id: string;
  unread: boolean;
  read_through_at: string | null;
  last_source: string | null;
  read_count: number;
}

/** Mark a conversation read or unread for the calling user — the portal's own
 *  marker (opening a thread marks it read automatically; this is "Mark as unread"
 *  and its inverse). Gmail labels are not touched. */
export async function setMailThreadRead(threadId: string, read: boolean): Promise<ThreadReadStatus> {
  const { data } = await api.post<ThreadReadStatus>(
    `${BASE}/mail/thread/${encodeURIComponent(threadId)}/read`,
    { read },
  );
  return data;
}

/** The mailbox views the panel can list (Drafts is separate: listMailDrafts). */
export type MailFolder = 'inbox' | 'sent' | 'trash';

/** List one folder's threads in the bound mailbox (read live via Gmail),
 *  optionally filtered by a Gmail search string and paged via page_token. */
export async function listMailThreads(
  opts: { limit?: number; folder?: MailFolder; search?: string; pageToken?: string } = {},
): Promise<MailThreadList> {
  const params: Record<string, string | number> = {};
  if (opts.limit != null) params.limit = opts.limit;
  if (opts.folder) params.folder = opts.folder;
  if (opts.search) params.search = opts.search;
  if (opts.pageToken) params.page_token = opts.pageToken;
  const { data } = await api.get<MailThreadList>(`${BASE}/mail/threads`, { params });
  return data;
}

/** Archive a conversation (out of the inbox; still searchable). */
export async function archiveMailThread(threadId: string): Promise<void> {
  await api.post(`${BASE}/mail/thread/${encodeURIComponent(threadId)}/archive`);
}

/** Move a conversation to Trash (recoverable in Gmail for ~30 days). */
export async function trashMailThread(threadId: string): Promise<void> {
  await api.post(`${BASE}/mail/thread/${encodeURIComponent(threadId)}/trash`);
}

/** Discard a draft permanently (the Drafts folder's delete action). */
export async function discardMailDraft(draftId: string): Promise<void> {
  await api.delete(`${BASE}/mail/draft/${encodeURIComponent(draftId)}`);
}

/** Read one thread live, mapped to the portal ChainDetail shape (for EmailChainThread). */
export async function getMailThread(threadId: string): Promise<ChainDetail> {
  const { data } = await api.get<ChainDetail>(
    `${BASE}/mail/thread/${encodeURIComponent(threadId)}`,
  );
  return data;
}

/** One proposed draft (the agent's proposals live in the bound mailbox's Drafts). */
export interface MailDraftSummary {
  draft_id: string;
  message_id: string | null;
  thread_id: string | null;
  to: string | null;
  /** Cc/Bcc already on the draft — the edit composer MUST rehydrate these:
   *  its save-before-send replaces the whole Gmail draft, so a field it
   *  doesn't carry is silently stripped from the delivered email. */
  cc?: string | null;
  bcc?: string | null;
  subject: string | null;
  snippet: string;
  /** Full proposed-reply body (not the truncated snippet), for the draft card. */
  body_text: string | null;
  body_html: string | null;
  /** Files attached to the draft (agent-attached product docs or John's uploads). */
  attachments?: AttachmentMeta[];
}

export interface MailDraftList {
  account_email: string;
  drafts: MailDraftSummary[];
}

export interface SendDraftResult {
  sent: boolean;
  sandbox: boolean;
  message_id: string | null;
  thread_id: string | null;
  sent_email_id: string | null;
  /** Set when a sandbox env really delivered — to this redirect test address. */
  redirect_email?: string | null;
}

/** List the proposed drafts in the bound mailbox. */
export async function listMailDrafts(): Promise<MailDraftList> {
  const { data } = await api.get<MailDraftList>(`${BASE}/mail/drafts`);
  return data;
}

/** Human-send a proposed draft (the agent never sends). Sandbox-safe on staging. */
export async function sendMailDraft(draftId: string): Promise<SendDraftResult> {
  const { data } = await api.post<SendDraftResult>(
    `${BASE}/mail/draft/${encodeURIComponent(draftId)}/send`,
  );
  return data;
}

// --- Attachments + human compose (download / upload / create + update draft) ---

/**
 * Fetch one inbound attachment's bytes from the bound mailbox, live from Gmail
 * (by message_id + attachment_id). Wired into EmailChainThread as its
 * `downloadAttachment` so the panel's attachment chips open/preview.
 */
export async function downloadMailAttachment(
  messageId: string,
  attachmentId: string,
  filename?: string,
): Promise<Blob> {
  // The filename rides along so the backend can re-resolve the part when the
  // attachment id has gone stale (Gmail regenerates ids on every fetch).
  const { data } = await api.get(
    `${BASE}/mail/message/${encodeURIComponent(messageId)}/attachment/${encodeURIComponent(attachmentId)}`,
    { responseType: 'blob', params: filename ? { filename } : undefined },
  );
  return data as Blob;
}

/** An uploaded attachment, ready to ride along on a draft (compose-upload shape). */
export interface MailComposeAttachment {
  file_id: string;
  filename: string;
  content_type: string;
  size_bytes: number;
  url: string;
}

/**
 * Upload a file John is attaching to a draft: get a GCS signed-PUT URL from the
 * co-pilot endpoint, PUT the bytes straight to GCS, and return the public URL +
 * metadata to send with the draft. Mirrors the orders-portal compose upload.
 */
export async function uploadMailAttachment(file: File): Promise<MailComposeAttachment> {
  const { data } = await api.post<{
    file_id: string;
    upload_url: string;
    public_url: string;
    filename: string;
    content_type: string;
    size_bytes: number;
  }>(`${BASE}/mail/upload-attachment`, {
    filename: file.name,
    content_type: file.type || 'application/octet-stream',
    size_bytes: file.size,
  });

  // fetch() does NOT throw on HTTP errors — check .ok explicitly. The signed URL
  // binds the Content-Type, so the PUT header must match the server's value.
  const put = await fetch(data.upload_url, {
    method: 'PUT',
    headers: { 'Content-Type': data.content_type, 'x-goog-acl': 'public-read' },
    body: file,
  });
  if (!put.ok) {
    const detail = await put.text().catch(() => '');
    throw new Error(`Attachment upload failed (${put.status}). ${detail}`.trim());
  }

  return {
    file_id: data.file_id,
    filename: data.filename,
    content_type: data.content_type,
    size_bytes: data.size_bytes,
    url: data.public_url,
  };
}

/** Payload to create or replace a human-authored draft (compose / reply / forward / edit). */
export interface ComposeDraftPayload {
  body: string;
  to?: string | null;
  subject?: string | null;
  body_html?: string | null;
  cc?: string[] | null;
  bcc?: string[] | null;
  /** Reply into this thread — recipient/subject/threading derived when omitted. */
  thread_id?: string | null;
  /** Forwarding: the source message whose files ride along (create only). */
  forward_from_message_id?: string | null;
  attachments?: MailComposeAttachment[];
  /**
   * Update only: which of the draft's EXISTING files survive this edit
   * (matched server-side by filename+size). null/omitted keeps them all;
   * a list keeps only these — how the composer removes a saved attachment.
   */
  kept_attachments?: { filename: string; size: number | null }[] | null;
}

/**
 * Autocomplete contacts for the composer's To/Cc/Bcc fields. Same company-wide
 * search the orders portal uses, exposed behind the tech-support door.
 */
export async function searchMailRecipients(
  query: string,
  limit: number = 15,
): Promise<RecipientResult[]> {
  if (query.trim().length < 2) return [];
  const { data } = await api.get<RecipientResult[]>(`${BASE}/mail/recipients`, {
    params: { q: query, limit },
  });
  return data;
}

/** Create a human-authored draft (John composes a new email or a reply). */
export async function createMailDraft(
  payload: ComposeDraftPayload,
): Promise<MailDraftSummary> {
  const { data } = await api.post<MailDraftSummary>(`${BASE}/mail/draft`, payload);
  return data;
}

/** Replace a draft's contents in place (John edits the agent's draft, or his own). */
export async function updateMailDraft(
  draftId: string,
  payload: ComposeDraftPayload,
): Promise<MailDraftSummary> {
  const { data } = await api.put<MailDraftSummary>(
    `${BASE}/mail/draft/${encodeURIComponent(draftId)}`,
    payload,
  );
  return data;
}

// --- Reply templates: John's house-standard canned replies -----------------

/** One shared reply template John can insert into a draft (the agent uses the
 *  same set via its playbook). `when_to_use` guides the picker; `body` may carry
 *  {name}/{state}/{product}/{problem} placeholders for John to fill. */
export interface ReplyTemplate {
  id: string;
  name: string;
  category: string;
  when_to_use: string;
  body: string;
}

export interface ReplyTemplateList {
  templates: ReplyTemplate[];
}

/** List the shared reply templates available in the picker. Insertion is
 *  client-side (at the cursor in the composer) — persistence rides the normal
 *  create/update-draft endpoints, so there is no server-side insert call. */
export async function listTemplates(): Promise<ReplyTemplate[]> {
  const { data } = await api.get<ReplyTemplateList>(`${BASE}/templates`);
  return data.templates;
}

// --- Signature: John's configurable sign-off, auto-appended to sent replies ---

export interface MailSignature {
  signature_html: string;
}

/** Get John's signature (seeds a pre-populated default on first use). */
export async function getSignature(): Promise<MailSignature> {
  const { data } = await api.get<MailSignature>(`${BASE}/signature`);
  return data;
}

/** Save John's signature HTML. */
export async function updateSignature(signatureHtml: string): Promise<MailSignature> {
  const { data } = await api.put<MailSignature>(`${BASE}/signature`, {
    signature_html: signatureHtml,
  });
  return data;
}

/** Reset the signature to the corporate-mandate format (server re-renders from the user record). */
export async function resetSignature(): Promise<MailSignature> {
  const { data } = await api.post<MailSignature>(`${BASE}/signature/reset`);
  return data;
}

/**
 * Open the SSE stream for one Tech Support Co-Pilot turn. Invokes `onEvent` for
 * every parsed frame; resolves when the server closes the stream (after `done`).
 * Throws only on a non-OK HTTP response — turn-level failures arrive as `error`
 * frames. The profile is forced server-side, so no `profile_id` is sent.
 */
export async function streamTechSupportMessage(
  conversationId: string,
  prompt: string,
  onEvent: (evt: ConsoleEvent) => void,
  signal?: AbortSignal,
  model?: string,
): Promise<void> {
  // Carry the selected model (last-turn-wins, persisted server-side); omitted ->
  // keep the conversation's current model. The profile stays forced server-side,
  // so no profile_id is sent. The shared loop handles the 401 refresh-and-retry.
  const payload: { prompt: string; model?: string } = { prompt };
  if (model) payload.model = model;
  return streamConsoleSse(
    `/api/tech-support-copilot/conversations/${conversationId}/message`,
    payload,
    onEvent,
    signal,
  );
}

/**
 * Whether to warn the user that the conversation's earliest turns are starting to
 * fall out of the agent's replayed-history window. The Tech Support Co-Pilot
 * replays only the last `cap` turns to the model each turn (the locked profile's
 * `history_max_turns`); once the user has sent `cap` messages, the next turn
 * begins dropping the oldest one, so earlier details can be forgotten. Pure +
 * unit-tested. A `cap` of null/undefined/<=0 (unbounded replay) never warns.
 */
export function shouldWarnHistoryFalloff(
  userTurns: number,
  cap: number | null | undefined,
): boolean {
  return !!cap && cap > 0 && userTurns >= cap;
}
